"use client";

import { useState } from "react";
import { HelpCircle, X } from "lucide-react";
import clsx from "clsx";

/**
 * Inline help affordance — a small "?" button next to a builder field that
 * opens a popover with the explainer + an example.
 *
 * Used across the per-type builders to surface CEFR-level best practices
 * without bloating the editor with always-visible text. Accessibility:
 * the button is `aria-describedby` linked to a live region so screen
 * readers announce the help text when toggled.
 */
export interface HelpTipProps {
  title: string;
  description: string;
  example?: string;
  /** Where the button is rendered. Used to pick an icon size. */
  size?: "xs" | "sm";
}

export function HelpTip({ title, description, example, size = "xs" }: HelpTipProps) {
  const [open, setOpen] = useState(false);
  const id = `helptip-${title.replace(/\s+/g, "-").toLowerCase()}`;

  return (
    <span className="relative inline-flex">
      <button
        type="button"
        aria-label={`Help for ${title}`}
        aria-expanded={open}
        aria-describedby={open ? id : undefined}
        onClick={() => setOpen((v) => !v)}
        className={clsx(
          "inline-flex items-center justify-center rounded-full text-[#8E8E93] hover:text-[#FAFAFA] hover:bg-white/10",
          size === "xs" ? "h-4 w-4" : "h-5 w-5",
        )}
      >
        <HelpCircle size={size === "xs" ? 11 : 13} />
      </button>
      {open && (
        <>
          <button
            type="button"
            aria-label="Close help"
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-40 bg-transparent cursor-default"
          />
          <div
            id={id}
            role="tooltip"
            className="absolute z-50 left-0 top-full mt-1.5 w-72 rounded-lg border border-[rgba(255,255,255,0.12)] bg-[#1C1C1E] p-3 shadow-[0_12px_32px_rgba(0,0,0,0.6)]"
          >
            <div className="flex items-start justify-between gap-2 mb-1.5">
              <p className="text-xs font-bold text-[#FFD60A]">{title}</p>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Dismiss"
                className="rounded p-0.5 text-[#8E8E93] hover:bg-white/10"
              >
                <X size={12} />
              </button>
            </div>
            <p className="text-[11px] text-[#FAFAFA] leading-relaxed">{description}</p>
            {example && (
              <pre className="mt-2 rounded bg-[#0A0A0A] border border-[rgba(255,255,255,0.06)] p-2 text-[10px] font-mono text-[#8E8E93] whitespace-pre-wrap">
                {example}
              </pre>
            )}
          </div>
        </>
      )}
    </span>
  );
}

/**
 * Static catalogue of help copy keyed by exercise type. Keeps the
 * explainer text next to the component code so it stays in sync.
 */
export const EXERCISE_TYPE_HELP: Record<
  string,
  { title: string; description: string; example?: string }
> = {
  multiple_choice: {
    title: "Multiple choice",
    description:
      "Add 2-4 plausible distractors. Use the radio button on the right to mark the single correct answer. ✨ Generate will suggest 4 distractors based on your prompt.",
    example: `prompt: "Was bedeutet Guten Morgen?"
options: ["Good morning", "Good night", ...]
correctIndex: 0`,
  },
  fill_blank: {
    title: "Fill in the blank",
    description:
      "Type 5+ underscores _____ to mark where the learner fills in. Add every acceptable spelling (case + form variants) — the first match wins.",
    example: `sentence: "Ich heiße _____."
acceptableAnswers: ["Anna", "anna"]`,
  },
  matching: {
    title: "Matching pairs",
    description:
      "Pairs shuffle on the learner side when shuffle-on-render is on (default). Add 3-8 pairs for a meaningful exercise.",
    example: `pairs: [
  { de: "Hund", en: "dog" },
  { de: "Katze", en: "cat" }
]`,
  },
  dictation: {
    title: "Dictation",
    description:
      "Upload or record a clear recording ≤ 10 seconds. The listen-count caps replays before a forced wrong answer. ✨ Transcribe auto-fills the canonical answer.",
  },
  ordering: {
    title: "Word ordering",
    description:
      "Type the canonical sentence once — the bank auto-fills and shuffles. Drag tokens from the bank into the sentence rail to define the correct order.",
    example: `canonical: "Ich lerne Deutsch"
tokens: ["Ich", "lerne", "Deutsch"]
correctOrder: ["Ich", "lerne", "Deutsch"]`,
  },
  audio_mcq: {
    title: "Audio MCQ",
    description:
      "Like MCQ but with an audio prompt. Choices can mix text / audio / image kinds — useful for sound-alike discrimination exercises.",
  },
  free_answer: {
    title: "Free answer",
    description:
      "Gemini grades this with the rubric you define. Reference answer is guidance, not exact-match. Aim for 2-4 rubric criteria summing to 100%.",
    example: `prompt: "Translate: I am learning German."
referenceAnswer: "Ich lerne Deutsch."
rubric: [
  { criterion: "Accuracy", weight: 60 },
  { criterion: "Grammar", weight: 40 }
]`,
  },
  free_writing: {
    title: "Free writing",
    description:
      "Long-form essay. The word-count band (min/max) is enforced — submissions outside the band are penalised. The rubric drives the score.",
    example: `prompt: "Describe your morning routine in 80-120 words."
minWords: 80
maxWords: 120`,
  },
};